import React from 'react'
import { Button, Typography } from 'antd'
import { useIntl } from 'react-intl'

import IntlMessages from '../../../../util/IntlMessages'
import { GoogleIcon, SlackIcon, TeamsIcon } from '../../../../components/icons'
import { PLATFORM_BRAND_NAME } from '../platform-brand'
import { DetectedPlatform, PlatformStepSource } from '../types'

const { Paragraph, Text, Link } = Typography

const PLATFORM_ICON: Record<DetectedPlatform, React.ReactElement> = {
  slack: <SlackIcon />,
  microsoft: <TeamsIcon />,
  google: <GoogleIcon />,
}

const PLATFORM_QA: Record<DetectedPlatform, string> = {
  slack: 'slack-connect',
  microsoft: 'microsoft-connect',
  google: 'google-connect',
}

export interface IPlatformStepProps {
  platform: DetectedPlatform
  source: PlatformStepSource
  email?: string
  organizationName?: string
  isBusy: boolean
  onPlatformSignin: () => void
  onChangeEmail: () => void
  onDifferentMethod: () => void
}

const PlatformStep = ({ platform, source, email, organizationName, isBusy, onPlatformSignin, onChangeEmail, onDifferentMethod }: IPlatformStepProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const brand = PLATFORM_BRAND_NAME[platform]

  // The organisation line belongs to a lookup that just ran. A remembered visit did not
  // run one, and a focused-platform link never had an address to look up.
  const showOrganizationLine = source === 'lookup'

  return (
    <>
      {email && (
        <div className="signin-account-row">
          <Text strong data-qa="signin-resolved-email">
            {email}
          </Text>
          <Link onClick={onChangeEmail} data-qa="signin-change-email">
            <IntlMessages id="app.change" />
          </Link>
        </div>
      )}
      {showOrganizationLine && (
        <Paragraph type="secondary" data-qa="signin-platform-hint">
          {organizationName
            ? formatMessage({ id: 'signin.platformStep.orgUsesPlatform' }, { organizationName, platform: brand })
            : formatMessage({ id: 'signin.platformStep.usesPlatform' }, { platform: brand })}
        </Paragraph>
      )}
      <Button size="large" type="primary" icon={PLATFORM_ICON[platform]} onClick={onPlatformSignin} disabled={isBusy} loading={isBusy} data-qa={PLATFORM_QA[platform]} block>
        {formatMessage({ id: 'signin.signInWithPlatform' }, { platform: brand })}
      </Button>
      {/* A platform named by the URL arrives with no address, so there is no "Change" row to
          leave by. The other two sources have one. */}
      {source === 'query' && (
        <Paragraph className="signin-other-options">
          <Link onClick={onDifferentMethod} data-qa="signin-different-method">
            <IntlMessages id="signin.platformStep.differentMethod" />
          </Link>
        </Paragraph>
      )}
    </>
  )
}

export default PlatformStep
